import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { clientApi, WorkspaceListItem } from '@/api/client'
import { qk } from '@/api/queryKeys'
import { getDefaultWatchlistId } from '@/auth/session'
import Delta from '@/components/Delta'
import ErrorBox from '@/components/ErrorBox'
import WorkspaceStateNotice from '@/components/WorkspaceStateNotice'
import Skeleton from '@/components/Skeleton'
import Topbar from '@/components/Topbar'
import { compactWorkspaceTitle } from '@/utils/workspaceDisplay'
import { timeAgo } from '@/utils/format'

function label(value?: string | null) {
  const key = String(value || 'unknown').toLowerCase()
  const labels: Record<string, string> = {
    active_monitoring: 'Live workspace',
    active: 'Live',
    live: 'Live',
    external_ready: 'Ready to share',
    external_distribution_ready: 'Ready to share',
    ready_for_external_distribution: 'Ready to share',
    'external ready': 'Ready to share',
    internal_review_ready: 'Ready',
    internal_ready: 'Ready',
    ready: 'Ready',
    draft: 'Draft',
    created: 'Draft',
    workspace_in_execution: 'Building workspace',
    executing: 'Building workspace',
    batch_submitted: 'Forecasts running',
    layer_1_batch_ready: 'Ready for forecast run',
    answer_ready: 'Current read ready',
    answered: 'Current read ready',
    answered_with_caveats: 'Current read ready',
    no_changes_yet: 'No changes yet',
    not_active: 'Not active',
    forecasting: 'Building workspace',
    // Internal review vocabulary stays server-side. "In review" implied
    // someone ELSE was reviewing — false in self-serve. "Needs attention"
    // names the client's own next action (the flagged quality checks).
    not_ready_for_internal_use: 'Needs attention',
    reviewed_not_ready: 'Needs attention',
    unknown: 'Status pending',
  }
  return labels[key] || String(value || 'unknown').replaceAll('_', ' ')
}

function fmtDate(value?: string | null) {
  if (!value) return null
  const d = new Date(value)
  // No seconds in cards — "6/11/2026, 1:57:17 AM" is log output, not UI copy.
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

function forecastProgress(ws: WorkspaceListItem) {
  const total = Math.max(0, Number(ws.generated_count || 0))
  const done = Math.max(0, Number(ws.forecasted_count || 0))
  if (!total) return 0
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)))
}

// Portfolio-level KPIs that used to live on the standalone Dashboard ("Home").
// Now that the workspace list IS the home, we surface the same cross-watchlist
// summary as a compact strip in the page header.
function PortfolioKpis() {
  const [watchlistId, setWatchlistId] = useState<string | null>(() => getDefaultWatchlistId())

  const watchlistListQuery = useQuery({
    queryKey: qk.watchlists.list(),
    queryFn: () => clientApi.watchlists(),
    enabled: !watchlistId,
    staleTime: 60_000,
  })
  useEffect(() => {
    if (watchlistId) return
    const wls = watchlistListQuery.data
    if (Array.isArray(wls) && wls.length > 0) setWatchlistId(wls[0].watchlist_id)
  }, [watchlistId, watchlistListQuery.data])

  const summaryQuery = useQuery({
    queryKey: qk.watchlists.summary(watchlistId || ''),
    queryFn: () => clientApi.watchlistSummary(watchlistId as string),
    enabled: !!watchlistId,
    staleTime: 30_000,
  })
  const summary = summaryQuery.data ?? null
  if (!summary) return null
  const topMover = summary.biggest_movers?.[0]

  return (
    <div className="workspace-kpi-strip" aria-label="Portfolio summary">
      <Link className="workspace-kpi" to="/universe">
        <span>Items tracked</span>
        <strong>{summary.total_items}</strong>
      </Link>
      <div className="workspace-kpi">
        <span>Forecasts (24h)</span>
        <strong>{summary.forecasts_updated_24h}</strong>
      </div>
      {summary.avg_abs_edge !== null && (
        <div className="workspace-kpi">
          <span>Avg |edge|</span>
          <strong>{(summary.avg_abs_edge * 100).toFixed(1)}%</strong>
        </div>
      )}
      {topMover && Math.abs(Number(topMover.delta) || 0) >= 0.005 && (
        <Link className="workspace-kpi mover" to={`/items/${topMover.item_id}`} title={topMover.label || ''}>
          <span>Top mover</span>
          <strong className="workspace-kpi-mover-label">{topMover.label}</strong>
          <Delta value={topMover.delta} />
        </Link>
      )}
    </div>
  )
}

// Unseen changes first, then live workspaces, then most-recently-changed:
// the home page is a briefing ("what moved since you looked?"), not an
// inventory.
function workspaceSort(a: WorkspaceListItem, b: WorkspaceListItem) {
  const unseenA = (a as any).changed_since_view ? 1 : 0
  const unseenB = (b as any).changed_since_view ? 1 : 0
  if (unseenA !== unseenB) return unseenB - unseenA
  const liveA = a.lifecycle_status === 'active_monitoring' ? 1 : 0
  const liveB = b.lifecycle_status === 'active_monitoring' ? 1 : 0
  if (liveA !== liveB) return liveB - liveA
  const tA = new Date((a as any).latest_change_at || a.updated_at || 0).getTime() || 0
  const tB = new Date((b as any).latest_change_at || b.updated_at || 0).getTime() || 0
  return tB - tA
}

function workspaceMatches(ws: WorkspaceListItem, query: string) {
  if (!query) return true
  const q = query.toLowerCase()
  return [ws.display_title, ws.title, ws.original_question, ws.headline, ws.display_subtitle]
    .some((field) => String(field || '').toLowerCase().includes(q))
}

export default function Workspaces() {
  const nav = useNavigate()
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[] | null>(null)
  const [error, setError] = useState<any>(null)
  const [query, setQuery] = useState('')
  const [ask, setAsk] = useState('')
  // The core action: a typed question hands off to the build flow (?q= seed).
  const goAsk = () => {
    const qv = ask.trim()
    nav(qv ? `/workspaces/new?q=${encodeURIComponent(qv)}` : '/workspaces/new')
  }

  useEffect(() => {
    let cancelled = false
    clientApi.workspaces()
      .then((res) => { if (!cancelled) setWorkspaces(res.workspaces || []) })
      .catch((e) => { if (!cancelled) setError(e) })
    return () => { cancelled = true }
  }, [])

  const visible = workspaces ? workspaces.filter((ws) => workspaceMatches(ws, query.trim())).sort(workspaceSort) : null

  return (
    <div className="container">
      <Topbar subtitle="Forecast Workspaces" right={<Link className="btn primary" to="/workspaces/new">New workspace</Link>} />
      {error && <ErrorBox error={error} />}

      <div className="workspace-hero">
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 850 }}>Your home</div>
        <h1>Ask a question, get a living read</h1>
        <form onSubmit={(e) => { e.preventDefault(); goAsk() }} style={{ marginTop: 12, maxWidth: 760 }}>
          <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
            <input
              className="input"
              style={{ flex: 1 }}
              value={ask}
              onChange={(e) => setAsk(e.target.value)}
              placeholder="e.g. US large-cap dividend stocks with a payout ratio under 60%"
              aria-label="Ask a market question to build a workspace"
            />
            <button className="btn primary" type="submit">Build →</button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            How it works: ask a question → review the companies Cenva scopes (~2–5 min) → it builds a living, evidence-backed dashboard you can monitor.
          </p>
        </form>
        {/* Digest strip: one glance answers "do I need to go deeper today?" */}
        {(() => {
          const changed = (workspaces || []).filter((ws) => (ws as any).changed_since_view)
          if (!changed.length) return null
          return (
            <div className="row" style={{ gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="pill ok">Since you last looked</span>
              <span className="muted" style={{ fontSize: 13 }}>
                {changed.length === 1 ? '1 workspace changed' : `${changed.length} workspaces changed`}
              </span>
              {changed.slice(0, 3).map((ws) => (
                <Link key={ws.workspace_id} className="pill" to={`/workspaces/${ws.workspace_id}`} style={{ textDecoration: 'none' }}>
                  {(ws.display_title || ws.title || 'Workspace').slice(0, 44)}
                </Link>
              ))}
            </div>
          )
        })()}
        {/* The KPI strip aggregates the whole watchlist (asks included);
            next to "No workspaces yet" it reads as a contradiction. */}
        {workspaces?.length ? <PortfolioKpis /> : null}
      </div>

      {!workspaces && !error ? (
        <div className="grid" style={{ marginTop: 16 }}><Skeleton variant="card" count={4} /></div>
      ) : null}

      {workspaces?.length === 0 ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>No workspaces yet</h2>
          <p className="muted">Create one from a broad client question.</p>
          <Link className="btn primary" to="/workspaces/new">Create workspace</Link>
        </div>
      ) : null}

      {workspaces?.length ? (
        <h2 style={{ marginTop: 22, marginBottom: 0 }}>
          Your dashboards <span className="muted" style={{ fontWeight: 400, fontSize: 16 }}>({workspaces.length})</span>
        </h2>
      ) : null}

      {workspaces && workspaces.length > 3 ? (
        <input
          type="search"
          placeholder="Search workspaces…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginTop: 12, width: '100%', maxWidth: 420 }}
          aria-label="Search workspaces"
        />
      ) : null}

      {workspaces?.length && visible && visible.length === 0 ? (
        <p className="muted" style={{ marginTop: 16 }}>No workspaces match “{query.trim()}”.</p>
      ) : null}

      {visible?.length ? (
        <div className="workspace-question-list" style={{ marginTop: 16 }}>
          {visible.map((ws) => {
            const title = ws.display_title || compactWorkspaceTitle(ws.title, ws.original_question, ws.headline)
            const progress = forecastProgress(ws)
            const publicationStatus = String(ws.publication_status || '').toLowerCase()
            const externalReady = ['external_ready', 'external_distribution_ready', 'ready_for_external_distribution'].includes(publicationStatus)
            const live = ws.lifecycle_status === 'active_monitoring'
            const ready = live
              ? 'Live'
              : externalReady
                ? 'Ready to share'
                : ws.readiness_status || ws.answer_state || ws.status
            const statusKey = String(ws.status || '').toLowerCase()
            // The list now leads with the read, so only surface the raw status
            // pill while the workspace is still being built. Once there's a
            // read, the readiness pill on the right carries the state.
            const building = ['workspace_in_execution', 'executing', 'batch_submitted', 'layer_1_batch_ready', 'created', 'draft'].includes(statusKey)
            // The "read" line that should lead the card.
            const readLine = ws.headline || ws.display_subtitle || ws.original_question
            const updated = ws.updated_at || ws.next_monitor_run_at
            return (
              <Link key={ws.workspace_id} className="card workspace-list-card" to={`/workspaces/${ws.workspace_id}`}>
                <div className="row workspace-list-main-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 style={{ marginTop: 0 }}>{title}</h2>
                    {/* Lead with the change when there is an unseen one — the
                        delta is the reason to open the workspace today. */}
                    {(ws as any).changed_since_view && (ws as any).latest_change_summary ? (
                      <p className="workspace-list-read" style={{ color: 'var(--accent, #00c8a0)' }}>
                        <span className="pill ok" style={{ marginRight: 6 }}>New</span>
                        {String((ws as any).latest_change_summary).slice(0, 160)}
                      </p>
                    ) : null}
                    {readLine ? <p className="workspace-list-read">{readLine}</p> : null}
                    {ws.headline && ws.original_question && ws.headline !== ws.original_question ? (
                      <p className="muted workspace-list-original">Question: {ws.original_question}</p>
                    ) : null}
                    <div className="row workspace-list-tags" style={{ gap: 6, marginTop: 8 }}>
                      {building ? <span className="pill">{label(ws.status)}</span> : null}
                      {ws.horizon ? <span className="pill">{ws.horizon}</span> : null}
                    </div>
                  </div>
                  <div className="workspace-list-status">
                    <span className={live || externalReady ? 'pill ok' : 'pill'}>{label(ready)}</span>
                    {/* Never render a "next ..." date that's already in the past —
                        an overdue schedule reads as a broken promise. */}
                    {updated ? (
                      <span className="muted workspace-list-updated">
                        {live && ws.next_monitor_run_at && new Date(ws.next_monitor_run_at).getTime() > Date.now()
                          ? `next check ${fmtDate(ws.next_monitor_run_at)}`
                          : live && ws.next_monitor_run_at
                            ? 'check due'
                            : `updated ${timeAgo(updated)}`}
                      </span>
                    ) : null}
                  </div>
                </div>
                {/* A stuck or reworked workspace explains itself here rather than
                    rendering as a card that silently never changes. */}
                <WorkspaceStateNotice state={ws.client_state} />
                {/* Build/health metrics demoted to a single quiet meta line. */}
                <div className="workspace-list-meta muted">
                  <span><strong>{ws.forecasted_count}/{ws.generated_count}</strong> forecasts<em><i style={{ width: `${progress}%` }} /></em></span>
                  <span>{ws.active_monitor_count ? `${ws.active_monitor_count} active monitors` : 'monitoring not active yet'}</span>
                  {ws.failed_count ? <span className="warn">{ws.failed_count} failed checks</span> : null}
                </div>
              </Link>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
